import React, { useState, useEffect } from 'react';
import { ethers } from 'ethers';
import styles from './Admin.module.css';
import Nav from '../../components/Nav/Nav';
import Footer from '../../components/Footer/Footer';
import Divider from '../../components/Divider/Divider';
import { showToast } from '../../utils/toast';
import { supabase } from '../../lib/supabase';

const CONTRACT_ADDRESS = "0x75a05f185E2EdA5e4DD9E1c878D598490Ca9A6Dc";
const OWNER_ADDRESS = "0xd15af52059a79f53D554B66Cc1c8b172C35622Ba";

const CONTRACT_ABI = [
  "function balanceOf(address owner) external view returns (uint256)",
  "function withdrawFees() external",
  "function owner() external view returns (address)",
  "function totalSupply() external view returns (uint256)"
];

const Admin = () => {
  const [address, setAddress] = useState(null);
  const [isOwner, setIsOwner] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [holderCount, setHolderCount] = useState(0);
  const [contractBalance, setContractBalance] = useState('0');
  const [withdrawing, setWithdrawing] = useState(false);
  const [searchAddress, setSearchAddress] = useState('');
  const [searchResult, setSearchResult] = useState(null);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    checkConnection();
    if (address) {
      checkOwnerStatus();
      fetchHolderCount();
      fetchContractBalance();
    }
  }, [address]);

  const checkConnection = async () => {
    if (window.ethereum) {
      try {
        const accounts = await window.ethereum.request({ method: 'eth_accounts' });
        if (accounts.length > 0) {
          setAddress(accounts[0]);
        }
      } catch (error) {
        console.error("Failed to get accounts:", error);
      }
    }
  };

  const connectWallet = async () => {
    if (window.ethereum) {
      try {
        setIsLoading(true);
        const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
        setAddress(accounts[0]);
        await checkOwnerStatus();
      } catch (error) {
        console.error("Failed to connect wallet:", error);
        showToast.error("Failed to connect wallet");
      } finally {
        setIsLoading(false);
      }
    } else {
      showToast.error("Please install MetaMask wallet");
    }
  };

  const checkOwnerStatus = async () => {
    if (!address) return;
    
    try {
      const provider = new ethers.providers.Web3Provider(window.ethereum);
      const contract = new ethers.Contract(CONTRACT_ADDRESS, CONTRACT_ABI, provider);
      
      // Verify contract owner
      const contractOwner = await contract.owner();
      const isContractOwner = address.toLowerCase() === contractOwner.toLowerCase();
      
      // Hardcoded address verification
      const isHardcodedOwner = address.toLowerCase() === OWNER_ADDRESS.toLowerCase();
      
      setIsOwner(isContractOwner || isHardcodedOwner);
    } catch (error) {
      console.error("Failed to verify owner status:", error);
      setIsOwner(false);
    }
  };

  const fetchHolderCount = async () => {
    try {
      const provider = new ethers.providers.Web3Provider(window.ethereum);
      const contract = new ethers.Contract(CONTRACT_ADDRESS, CONTRACT_ABI, provider);
      
      const totalSupply = await contract.totalSupply();
      setHolderCount(totalSupply.toNumber());
    } catch (error) {
      console.error("Failed to get holder count:", error);
      showToast.error("Failed to get holder count");
    }
  };

  const fetchContractBalance = async () => {
    try {
      const provider = new ethers.providers.Web3Provider(window.ethereum);
      const balance = await provider.getBalance(CONTRACT_ADDRESS);
      
      setContractBalance(ethers.utils.formatEther(balance));
    } catch (error) {
      console.error("Failed to get contract balance:", error);
      showToast.error("Failed to get contract balance");
    }
  };

  const withdrawFees = async () => {
    if (!isOwner) return;
    
    try {
      setWithdrawing(true);
      const provider = new ethers.providers.Web3Provider(window.ethereum);
      const signer = provider.getSigner();
      const contract = new ethers.Contract(CONTRACT_ADDRESS, CONTRACT_ABI, signer);
      
      const tx = await contract.withdrawFees();
      showToast.success("Withdrawal transaction submitted");
      
      await tx.wait();
      showToast.success("Withdrawal completed successfully");
      
      // Refresh the contract balance after withdrawal
      fetchContractBalance();
    } catch (error) {
      console.error("Withdrawal failed:", error);
      showToast.error(error.message || "Withdrawal failed");
    } finally {
      setWithdrawing(false);
    }
  };

  const searchUser = async () => {
    if (!searchAddress || !isOwner) return;
    
    try {
      setSearching(true);
      setSearchResult(null);
      
      // Convert address to lowercase for query
      const normalizedAddress = searchAddress.toLowerCase();
      
      const { data, error } = await supabase
        .from('users')
        .select('direct_referral_count, indirect_referral_count')
        .eq('address', normalizedAddress)
        .single();
      
      if (error) throw error;
      
      if (data) {
        setSearchResult(data);
      } else {
        showToast.error('User not found');
      }
    } catch (error) {
      console.error("Failed to search user:", error);
      showToast.error(error.message || "Search failed");
    } finally {
      setSearching(false);
    }
  };

  return (
    <div className={styles.adminContainer}>
      <Nav />
      <main className={styles.mainContent}>
        <div className={styles.container}>
          <h1 className={styles.title}>Admin Panel</h1>
          
          {!address ? (
            <div className={styles.connectSection}>
              <button 
                className={styles.connectButton} 
                onClick={connectWallet}
                disabled={isLoading}
              >
                {isLoading ? "Connecting..." : "Connect Wallet"}
              </button>
            </div>
          ) : !isOwner ? (
            <div className={styles.unauthorizedSection}>
              <p className={styles.warningText}>
                Only contract owner can access this page
              </p>
            </div>
          ) : (
            <div className={styles.adminSection}>
              <div className={styles.statsRow}>
                <div className={styles.statsCard}>
                  <h2 className={styles.cardTitle}>NFT Holders Count</h2>
                  <div className={styles.statValue}>{holderCount}</div>
                </div>
                
                <div className={styles.statsCard}>
                  <h2 className={styles.cardTitle}>Contract Balance</h2>
                  <div className={styles.statValue}>{contractBalance} <span className={styles.unitLabel}>BNB</span></div>
                </div>
              </div>
              
              <div className={styles.actionSection}>
                <button 
                  className={styles.withdrawButton}
                  onClick={withdrawFees}
                  disabled={withdrawing || parseFloat(contractBalance) === 0}
                >
                  {withdrawing ? "Processing..." : "Withdraw All Fees"}
                </button>
              </div>
              
              <div className={styles.searchSection}>
                <h2 className={styles.sectionTitle}>User Data Query</h2>
                <div className={styles.searchBox}>
                  <input
                    type="text"
                    className={styles.searchInput}
                    placeholder="Enter wallet address"
                    value={searchAddress}
                    onChange={(e) => setSearchAddress(e.target.value)}
                  />
                  <button 
                    className={styles.searchButton}
                    onClick={searchUser}
                    disabled={searching || !searchAddress}
                  >
                    {searching ? "Searching..." : "Search"}
                  </button>
                </div>
                
                {searchResult && (
                  <div className={styles.resultCard}>
                    <h3 className={styles.resultTitle}>Search Results</h3>
                    <div className={styles.resultItem}>
                      <span className={styles.resultLabel}>Direct Referrals:</span>
                      <span className={styles.resultValue}>{searchResult.direct_referral_count || 0}</span>
                    </div>
                    <div className={styles.resultItem}>
                      <span className={styles.resultLabel}>Indirect Referrals:</span>
                      <span className={styles.resultValue}>{searchResult.indirect_referral_count || 0}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </main>
      <Divider />
      <Footer />
      <Divider />
    </div>
  );
};

export default Admin;
